// batch-70 uitrusting-controle (FO-531, spec 231) - de campagnesoort
// UITRUSTINGSCONTROLE: iedere medewerker controleert zijn servicekoffer,
// PBM-koffer en accugereedschap tegen de lijst die de app van hem kent
// (aanwezig / mist / defect / niet ontvangen), tekent bij compleetheid zijn open
// bruikleenovereenkomsten en het magazijn krijgt één overzicht van wat ontbreekt.
//
// Eén bron voor de request-schema's (API en formulieren valideren met dezelfde
// schema's, CLAUDE.md "Validatie") en de DTO's van portal en backoffice. Geen
// kostprijs, marge of inkoopprijs in deze DTO's: de portal ziet uitsluitend wat
// hij heeft, het magazijn alleen de voorraadstand (aantallen).
//
// Conventies: ID-referenties `idParam` (geen `.cuid()`, legacy UUID's in prod);
// datums als ISO-instant (UTC), Europe/Amsterdam aan de rand.

import { z } from 'zod'
import { idParam } from './id-validators'
import { queryBoolean } from './zod-query'
import { BRUIKLEEN_TRIGGER_SOORTEN, type BruikleenTriggerSoort } from './overeenkomst-vervolg'
import type { CampagneDeelnameStatus, CampagneStatus } from './campagne'

// ─── Enums (spiegel van Prisma) ───────────────────────────────────────────────

export const UITRUSTING_SET_SOORTEN = ['SERVICEKOFFER', 'PBM_KOFFER', 'ACCUGEREEDSCHAP', 'KAR'] as const
export type UitrustingSetSoort = (typeof UITRUSTING_SET_SOORTEN)[number]
export const UITRUSTING_SET_SOORT_LABELS: Record<UitrustingSetSoort, string> = {
  SERVICEKOFFER: 'Servicekoffer',
  PBM_KOFFER: 'PBM-koffer',
  ACCUGEREEDSCHAP: 'Accugereedschap',
  KAR: 'Gereedschapskar',
}
/** De standaard set van een nieuwe campagne (volgorde = de stappen van de wizard). */
export const UITRUSTING_SET_SOORTEN_STANDAARD: readonly UitrustingSetSoort[] = ['SERVICEKOFFER', 'PBM_KOFFER', 'ACCUGEREEDSCHAP']

export const UITRUSTING_CONTROLE_SET_STATUSSEN = ['OPEN', 'GECONTROLEERD', 'NIET_ONTVANGEN', 'WEL_IN_BEZIT'] as const
export type UitrustingControleSetStatus = (typeof UITRUSTING_CONTROLE_SET_STATUSSEN)[number]
export const UITRUSTING_CONTROLE_SET_STATUS_LABELS: Record<UitrustingControleSetStatus, string> = {
  OPEN: 'Nog te controleren',
  GECONTROLEERD: 'Gecontroleerd',
  NIET_ONTVANGEN: 'Nog niet ontvangen',
  WEL_IN_BEZIT: 'Wel in bezit (magazijn controleert)',
}

export const UITRUSTING_CONTROLE_REGEL_STATUSSEN = ['OPEN', 'AANWEZIG', 'MIST', 'DEFECT', 'NIET_ONTVANGEN'] as const
export type UitrustingControleRegelStatus = (typeof UITRUSTING_CONTROLE_REGEL_STATUSSEN)[number]
export const UITRUSTING_CONTROLE_REGEL_STATUS_LABELS: Record<UitrustingControleRegelStatus, string> = {
  OPEN: 'Nog niet afgevinkt',
  AANWEZIG: 'Aanwezig',
  MIST: 'Mist',
  DEFECT: 'Defect',
  NIET_ONTVANGEN: 'Niet ontvangen',
}
/** De drie meldingen; de betekenis staat zo in de wizard (spec §C5). */
export const UITRUSTING_MELDINGEN = ['MIST', 'DEFECT', 'NIET_ONTVANGEN'] as const
export type UitrustingMelding = (typeof UITRUSTING_MELDINGEN)[number]
export const UITRUSTING_MELDING_UITLEG = 'Mist = gehad, nu kwijt · Defect = kapot · Niet ontvangen = nooit gekregen'

export const UITRUSTING_AFHANDELINGEN = ['AANGEVULD', 'VERVANGEN', 'BESTELD', 'REPARATIE', 'NVT', 'VERMIST', 'HERSTELD'] as const
export type UitrustingAfhandeling = (typeof UITRUSTING_AFHANDELINGEN)[number]
export const UITRUSTING_AFHANDELING_LABELS: Record<UitrustingAfhandeling, string> = {
  AANGEVULD: 'Aangevuld uit voorraad',
  VERVANGEN: 'Vervangen uit voorraad',
  BESTELD: 'Op de bestellijst',
  REPARATIE: 'Naar reparatie',
  NVT: 'Niet van toepassing',
  VERMIST: 'Als vermist afgeboekt',
  HERSTELD: 'Hersteld (klopt op naam)',
}

export const UITRUSTING_REGEL_BRONNEN = ['DRAGER_REGEL', 'MODULE_ONDERDEEL', 'UNIEK_ITEM', 'BULK', 'PAKKET_REGEL'] as const
export type UitrustingRegelBron = (typeof UITRUSTING_REGEL_BRONNEN)[number]

/** De acties van het magazijn op een melding (§E12). */
export const UITRUSTING_AFHANDEL_ACTIES = ['AANVULLEN', 'VERVANGEN', 'BESTELLEN', 'REPARATIE', 'NVT'] as const
export type UitrustingAfhandelActie = (typeof UITRUSTING_AFHANDEL_ACTIES)[number]
export const UITRUSTING_AFHANDEL_ACTIE_LABELS: Record<UitrustingAfhandelActie | 'LEDGER', string> = {
  AANVULLEN: 'Aanvullen',
  VERVANGEN: 'Vervangen',
  BESTELLEN: 'Bestellen',
  REPARATIE: 'Reparatie',
  NVT: 'Niet van toepassing',
  LEDGER: 'Zoeken in ledger',
}

/** De bruikleensoorten die een uitrustingscontrole aanbiedt (alle triggersoorten). */
export const UITRUSTING_OVEREENKOMST_SOORTEN = BRUIKLEEN_TRIGGER_SOORTEN
export const UITRUSTING_OVEREENKOMST_SOORTEN_STANDAARD: readonly BruikleenTriggerSoort[] = [
  'VOERTUIG_BERIJDER',
  'TELEFOON_SIM',
  'GEREEDSCHAP_SET',
  'KLEDING_PBM',
]
/** Korte naam van een overeenkomst in de wizard en het overzicht ("Auto", "Telefoon"). */
export const UITRUSTING_OVEREENKOMST_SOORT_LABELS: Record<BruikleenTriggerSoort, string> = {
  VOERTUIG_BERIJDER: 'Auto',
  TELEFOON_SIM: 'Telefoon',
  GEREEDSCHAP_SET: 'Gereedschap',
  LAPTOP: 'Laptop of tablet',
  SLEUTEL: 'Sleutel',
  KLEDING_PBM: 'Kleding en PBM',
}

/** Stand van een overeenkomst in de campagne (afgeleid uit de bruikleenstatus, §D9). */
export const UITRUSTING_OVEREENKOMST_STATUSSEN = [
  'GETEKEND',
  'TE_TEKENEN',
  'WACHT_OP_AANVULLING',
  'WORDT_OPGESTELD',
  'GEMELD_ONJUIST',
  'GEEN',
] as const
export type UitrustingOvereenkomstStatus = (typeof UITRUSTING_OVEREENKOMST_STATUSSEN)[number]
export const UITRUSTING_OVEREENKOMST_STATUS_LABELS: Record<UitrustingOvereenkomstStatus, string> = {
  GETEKEND: 'Getekend',
  TE_TEKENEN: 'Nog niet getekend',
  WACHT_OP_AANVULLING: 'Tekenen na aanvulling',
  WORDT_OPGESTELD: 'Wordt opgesteld',
  GEMELD_ONJUIST: 'Gemeld: klopt niet',
  GEEN: 'Niet van toepassing',
}

/** De vier spelregels uit de preview (§12 scherm 0); de standaard als `spelregels` leeg is. */
export const UITRUSTING_STANDAARD_SPELREGELS: ReadonlyArray<{ titel: string; tekst: string }> = [
  {
    titel: 'Iedereen heeft dezelfde basis.',
    tekst: 'Een standaard servicekoffer, een PBM-koffer en accugereedschap. Je ziet jouw lijst zoals die in de app staat.',
  },
  {
    titel: 'Vink af wat je hebt, meld wat mist of kapot is.',
    tekst: 'Magazijn vult aan of vervangt. Je hoeft niets zelf te bestellen.',
  },
  {
    titel: 'Compleet? Dan teken je.',
    tekst: 'De bruikleenovereenkomsten voor auto, telefoon, gereedschap en kleding staan klaar in de app; je controleert ze en tekent op je telefoon.',
  },
  {
    titel: 'Nog niets ontvangen?',
    tekst: 'Geef dat aan bij de set; dan komt die in de uitgiftelijst van magazijn.',
  },
]

/** De spelregels als markdown (seed-default in het formulier). */
export function standaardSpelregelsMarkdown(): string {
  return UITRUSTING_STANDAARD_SPELREGELS.map((r, i) => `${i + 1}. **${r.titel}** ${r.tekst}`).join('\n')
}

/**
 * Lees de spelregels uit markdown terug als lijst (genummerde regels, de vetgedrukte
 * kop optioneel). Leeg of onleesbaar = de standaard.
 */
export function leesSpelregels(markdown: string | null | undefined): ReadonlyArray<{ titel: string; tekst: string }> {
  const tekst = (markdown ?? '').trim()
  if (!tekst) return UITRUSTING_STANDAARD_SPELREGELS
  const regels = tekst
    .split(/\r?\n/)
    .map((r) => r.trim())
    .filter((r) => r.length > 0)
    .map((r) => r.replace(/^(\d+[.)]|[-*])\s+/, ''))
    .map((r) => {
      const m = /^\*\*(.+?)\*\*\s*(.*)$/.exec(r)
      return m ? { titel: m[1]!.trim(), tekst: m[2]!.trim() } : { titel: '', tekst: r }
    })
  return regels.length > 0 ? regels : UITRUSTING_STANDAARD_SPELREGELS
}

/** Keuring binnen zoveel dagen = automatisch het label "keuren" (§C5) en de tab Keuringen (§E12). */
export const UITRUSTING_KEURING_SIGNAAL_DAGEN = 60

// ─── Request-schema's: inrichten (backoffice) ────────────────────────────────

export const UITRUSTING_DEADLINE_NA_START = 'De deadline moet na de startdatum liggen'

const uniekeLijst = <T extends string>(waarden: readonly T[]): boolean => new Set(waarden).size === waarden.length

const uitrustingscontroleBasis = z.object({
  naam: z.string().trim().min(3, 'Vul een naam in (minimaal 3 tekens)').max(120),
  jaar: z.number().int().min(2024).max(2100),
  /** ISO-instant van de start (kaart + push). */
  startDatum: z.string().datetime(),
  /** ISO-instant; de API zet hem op het einde van de NL-dag (tot en met). */
  deadline: z.string().datetime(),
  /** Alleen via de knop "Vrijgeven" openen (startDatum is dan een plaatshouder). */
  handmatigOpenen: z.boolean(),
  doelgroep: z.enum(['ALLE_MEDEWERKERS', 'MET_UITRUSTING']),
  entityIds: z.array(idParam('entityId')).max(50),
  departmentIds: z.array(idParam('departmentId')).max(200),
  sets: z
    .array(z.enum(UITRUSTING_SET_SOORTEN))
    .min(1, 'Kies minstens één set om te controleren')
    .max(UITRUSTING_SET_SOORTEN.length)
    .refine(uniekeLijst, 'Elke set maar één keer'),
  overeenkomstSoorten: z
    .array(z.enum(BRUIKLEEN_TRIGGER_SOORTEN))
    .max(BRUIKLEEN_TRIGGER_SOORTEN.length)
    .refine(uniekeLijst, 'Elke overeenkomst maar één keer'),
  tekenenAlleenBijCompleet: z.boolean(),
  spelregels: z.string().trim().max(5000).nullable(),
  looptijdDagen: z.number().int().min(7, 'Minimaal een week').max(90, 'Maximaal 90 dagen'),
})

/** Aanmaken (CONCEPT). */
export const uitrustingscontroleAanmakenSchema = uitrustingscontroleBasis.refine(
  (d) => new Date(d.deadline) > new Date(d.startDatum),
  { message: UITRUSTING_DEADLINE_NA_START, path: ['deadline'] },
)
export type UitrustingscontroleAanmakenInput = z.infer<typeof uitrustingscontroleAanmakenSchema>

/**
 * Wijzigen: elk veld uitgeschreven en optioneel, zonder defaults (valkuil in
 * campagnes.md §9: nooit `.partial()`, "niet meegestuurd" mag nooit "terug naar
 * de standaard" worden). Na het vrijgeven toetst de API welke velden nog mogen.
 */
export const uitrustingscontroleWijzigenSchema = z
  .object({
    naam: uitrustingscontroleBasis.shape.naam.optional(),
    jaar: uitrustingscontroleBasis.shape.jaar.optional(),
    startDatum: uitrustingscontroleBasis.shape.startDatum.optional(),
    deadline: uitrustingscontroleBasis.shape.deadline.optional(),
    handmatigOpenen: z.boolean().optional(),
    doelgroep: uitrustingscontroleBasis.shape.doelgroep.optional(),
    entityIds: uitrustingscontroleBasis.shape.entityIds.optional(),
    departmentIds: uitrustingscontroleBasis.shape.departmentIds.optional(),
    sets: uitrustingscontroleBasis.shape.sets.optional(),
    overeenkomstSoorten: uitrustingscontroleBasis.shape.overeenkomstSoorten.optional(),
    tekenenAlleenBijCompleet: z.boolean().optional(),
    spelregels: uitrustingscontroleBasis.shape.spelregels.optional(),
    looptijdDagen: uitrustingscontroleBasis.shape.looptijdDagen.optional(),
  })
  .refine((d) => !(d.startDatum && d.deadline) || new Date(d.deadline) > new Date(d.startDatum), {
    message: UITRUSTING_DEADLINE_NA_START,
    path: ['deadline'],
  })
export type UitrustingscontroleWijzigenInput = z.infer<typeof uitrustingscontroleWijzigenSchema>

// ─── Request-schema's: de medewerker (portal) ────────────────────────────────

const toelichting = z.string().trim().max(500, 'Maximaal 500 tekens')
const fotoIds = z.array(z.string().uuid('Onbekende foto')).max(10, 'Maximaal 10 foto\'s')

/** Eén regel afvinken of melden (status, toelichting, foto's). */
export const controleRegelZetSchema = z.object({
  status: z.enum(UITRUSTING_CONTROLE_REGEL_STATUSSEN),
  toelichting: toelichting.nullable().optional(),
  fotoIds: fotoIds.optional(),
})
export type ControleRegelZetInput = z.infer<typeof controleRegelZetSchema>

/**
 * De set als geheel: "nog niet ontvangen", "ik heb hem wel" (bij een set die de app
 * niet kent), terug naar afvinken (`CONTROLEREN`), of alleen de losse opmerking.
 */
export const controleSetZetSchema = z
  .object({
    keuze: z.enum(['NIET_ONTVANGEN', 'WEL_IN_BEZIT', 'CONTROLEREN']).optional(),
    toelichting: toelichting.nullable().optional(),
    fotoIds: fotoIds.optional(),
    opmerking: z.string().trim().max(1000, 'Maximaal 1000 tekens').nullable().optional(),
  })
  .refine((d) => d.keuze !== 'WEL_IN_BEZIT' || (d.toelichting ?? '').trim().length >= 3, {
    message: 'Vertel kort van wie of wanneer je de set kreeg',
    path: ['toelichting'],
  })
export type ControleSetZetInput = z.infer<typeof controleSetZetSchema>

/** De QR op de koffer gescand (het token uit de scanner). */
export const controleScanSchema = z.object({
  token: z.string().trim().min(4, 'Onbekende QR-code').max(300),
  /** Bevestigd: "dit is koffer X van Y, klopt dat?" met ja beantwoord (wisselmelding). */
  wisselBevestigd: z.boolean().optional(),
})
export type ControleScanInput = z.infer<typeof controleScanSchema>

/**
 * A4: een teamleider of het magazijn legt de controle vast namens een medewerker
 * zonder telefoon, met reden. Per set de keuze en per regel de status; regels
 * die niet meekomen krijgen "aanwezig" als `rest` = AANWEZIG. Tekenen blijft persoonlijk.
 */
export const controleNamensSchema = z.object({
  employeeId: idParam('employeeId'),
  reden: z.string().trim().min(3, 'Geef een reden (minimaal 3 tekens)').max(500),
  sets: z
    .array(
      z.object({
        setId: idParam('setId'),
        keuze: z.enum(['NIET_ONTVANGEN', 'WEL_IN_BEZIT', 'CONTROLEREN']).optional(),
        toelichting: toelichting.nullable().optional(),
        regels: z
          // AANWEZIG mag expliciet: een eerdere melding van de medewerker terugzetten.
          .array(z.object({ regelId: idParam('regelId'), status: z.enum(['AANWEZIG', ...UITRUSTING_MELDINGEN]), toelichting: toelichting.nullable().optional() }))
          .max(500),
        /** Wat niet in `regels` staat: aanwezig afvinken. */
        rest: z.enum(['AANWEZIG']).optional(),
      }),
    )
    .max(20),
})
export type ControleNamensInput = z.infer<typeof controleNamensSchema>

/** "Klopt niet" bij een overeenkomst: niet tekenen, magazijn past aan. */
export const overeenkomstMeldenSchema = z.object({
  soort: z.enum(BRUIKLEEN_TRIGGER_SOORTEN),
  documentId: idParam('documentId').nullable().optional(),
  toelichting: z.string().trim().min(3, 'Vertel kort wat er niet klopt').max(1000),
})
export type OvereenkomstMeldenInput = z.infer<typeof overeenkomstMeldenSchema>

/** De overeenkomst van deze soort laten opstellen (automatisch genereren met de actuele inhoud). */
export const overeenkomstVoorbereidenSchema = z.object({
  soort: z.enum(BRUIKLEEN_TRIGGER_SOORTEN),
})
export type OvereenkomstVoorbereidenInput = z.infer<typeof overeenkomstVoorbereidenSchema>

// ─── Request-schema's: magazijn (backoffice) ─────────────────────────────────

export const UITRUSTING_AFHANDELEN_MAX = 200

/** Afhandelen per regel of in bulk (§E12): aanvullen, vervangen, bestellen, reparatie, n.v.t. */
export const uitrustingAfhandelenSchema = z
  .object({
    regelIds: z.array(idParam('regelId')).min(1, 'Kies minstens één regel').max(UITRUSTING_AFHANDELEN_MAX),
    actie: z.enum(UITRUSTING_AFHANDEL_ACTIES),
    /** Verplicht bij n.v.t. en reparatie (omschrijving van het defect). */
    reden: z.string().trim().max(1000).nullable().optional(),
    /** Reparatie: extra foto's van het magazijn (naast die van de medewerker). */
    fotoIds: z.array(z.string().uuid('Onbekende foto')).max(10).optional(),
  })
  .refine((d) => !(d.actie === 'NVT' || d.actie === 'REPARATIE') || (d.reden ?? '').trim().length >= 3, {
    message: 'Geef een reden (minimaal 3 tekens)',
    path: ['reden'],
  })
export type UitrustingAfhandelenInput = z.infer<typeof uitrustingAfhandelenSchema>

/**
 * "Zoeken in ledger" (§6.3): het exemplaar als vermist afboeken of herstellen, altijd met
 * reden. Bij bulk op naam (losse accu's en laders) is VERMIST een afboeking van `aantal`
 * stuks (integrator-review H1): dat aantal vult het magazijn daarna aan, niet de hele stand.
 */
export const uitrustingLedgerBesluitSchema = z.object({
  besluit: z.enum(['VERMIST', 'HERSTELD']),
  reden: z.string().trim().min(3, 'Geef een reden (minimaal 3 tekens)').max(1000),
  aantal: z.number({ error: 'Geef een aantal' }).int('Geef een heel aantal').min(1, 'Minimaal 1').max(10_000).optional(),
})
export type UitrustingLedgerBesluitInput = z.infer<typeof uitrustingLedgerBesluitSchema>

/** Uitgiftelijst: van de gekozen sets een samenstel-opdracht maken (FO-509/510). */
export const uitrustingSamenstelSchema = z.object({
  setIds: z.array(idParam('setId')).min(1, 'Kies minstens één set').max(100),
})
export type UitrustingSamenstelInput = z.infer<typeof uitrustingSamenstelSchema>

/** "Wel in bezit" of een set op de uitgiftelijst afhandelen (met reden), bv. na de uitgifte via het ledger. */
export const uitrustingSetAfhandelenSchema = z.object({
  setIds: z.array(idParam('setId')).min(1).max(100),
  reden: z.string().trim().min(3, 'Geef een reden (minimaal 3 tekens)').max(1000),
})
export type UitrustingSetAfhandelenInput = z.infer<typeof uitrustingSetAfhandelenSchema>

/** Overeenkomsten opnieuw laten genereren (bulk, na een melding of aanvulling). */
export const uitrustingHergenererenSchema = z.object({
  meldingIds: z.array(idParam('meldingId')).min(1, 'Kies minstens één melding').max(100),
})
export type UitrustingHergenererenInput = z.infer<typeof uitrustingHergenererenSchema>

// ─── Query-schema's ───────────────────────────────────────────────────────────

const pagina = {
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(100).default(25),
}

export const uitrustingOntbrekendQuerySchema = z.object({
  groepeer: z.enum(['onderdeel', 'medewerker']).default('onderdeel'),
  set: z.enum(UITRUSTING_SET_SOORTEN).optional(),
  melding: z.enum(UITRUSTING_MELDINGEN).optional(),
  /** OPEN = nog niet afgehandeld (standaard), AFGEHANDELD, of ALLE. */
  status: z.enum(['OPEN', 'AFGEHANDELD', 'ALLE']).default('OPEN'),
  departmentId: idParam('departmentId').optional(),
  zoek: z.string().trim().max(100).optional(),
  ...pagina,
})
export type UitrustingOntbrekendQuery = z.infer<typeof uitrustingOntbrekendQuerySchema>

export const uitrustingMedewerkersQuerySchema = z.object({
  /** ACHTERBLIJVER = niet gestart of concept (de tab Achterblijvers, ook voor de teamleider). */
  status: z.enum(['NIET_GESTART', 'CONCEPT', 'INGEDIEND', 'VERVALLEN', 'ACHTERBLIJVER']).optional(),
  /** Alleen medewerkers met een open melding of een set nog niet ontvangen. */
  metAfwijking: queryBoolean().optional(),
  departmentId: idParam('departmentId').optional(),
  zoek: z.string().trim().max(100).optional(),
  ...pagina,
})
export type UitrustingMedewerkersQuery = z.infer<typeof uitrustingMedewerkersQuerySchema>

export const uitrustingPaginaQuerySchema = z.object({ ...pagina })

// ─── DTO's: gedeeld ───────────────────────────────────────────────────────────

export interface UitrustingscontroleConfigDTO {
  sets: UitrustingSetSoort[]
  overeenkomstSoorten: BruikleenTriggerSoort[]
  tekenenAlleenBijCompleet: boolean
  /** De ingestelde markdown (null = de standaard). */
  spelregels: string | null
  /** De spelregels zoals de wizard ze toont (ingesteld of de standaard). */
  spelregelsLijst: Array<{ titel: string; tekst: string }>
  looptijdDagen: number
}

export interface ControleRegelDTO {
  id: string
  groep: string
  omschrijving: string
  detail: string | null
  aantal: number
  serienummer: string | null
  status: UitrustingControleRegelStatus
  toelichting: string | null
  fotoIds: string[]
  /** Keuring verlopen of binnen 60 dagen: label "keuren" (geen actie van de medewerker). */
  keuren: boolean
  /** ISO-datum van de volgende keuring of houdbaarheid, of null. */
  keuringTot: string | null
  /** Het exemplaar staat op naam van de medewerker (niet ontvangen loopt dan via het magazijn). */
  opNaam: boolean
  afhandeling: UitrustingAfhandeling | null
  /** Na de start toegevoegd (lijst bijgewerkt). */
  nieuw: boolean
}

export interface ControleSetTellersDTO {
  totaal: number
  open: number
  aanwezig: number
  mist: number
  defect: number
  nietOntvangen: number
}

export interface ControleSetDTO {
  id: string
  soort: UitrustingSetSoort
  soortLabel: string
  titel: string
  nummer: string | null
  bekendBijApp: boolean
  uitgegevenOp: string | null
  status: UitrustingControleSetStatus
  nietOntvangenToelichting: string | null
  welInBezitToelichting: string | null
  opmerking: string | null
  /** Er hangt een koffer met een eigen QR aan deze set (scannen kan). */
  heeftQr: boolean
  qrBevestigd: boolean
  wisselGemeld: boolean
  /** De set staat niet meer op naam; de meldingen blijven tot het magazijn ze afhandelt (alleen lezen). */
  bronVerdwenen: boolean
  groepen: Array<{ naam: string; regels: ControleRegelDTO[] }>
  tellers: ControleSetTellersDTO
  /** "compleet", "1 mist", "nog niet ontvangen" (pill in het overzicht). */
  samenvatting: string
  samenvattingToon: 'ok' | 'rood' | 'oranje' | 'neutraal'
}

export type UitrustingOvereenkomstActie = 'BEKIJKEN' | 'CONTROLEREN' | 'CONTROLEREN_EN_TEKENEN' | 'VOORBEREIDEN' | 'GEEN'

export interface UitrustingOvereenkomstRijDTO {
  soort: BruikleenTriggerSoort
  label: string
  /** "Auto · VW Transporter AB-123-C", "Gereedschap · servicekoffer + PBM-koffer". */
  titel: string
  /** De middelen die de overeenkomst dekt (zoals de medewerker ze ziet). */
  middelen: string[]
  status: UitrustingOvereenkomstStatus
  /** Subtekst: "Getekend 14-03-2025", "Tekenen na aanvulling: momentsleutel mist". */
  subtekst: string
  documentId: string | null
  triggerId: string | null
  getekendOp: string | null
  actie: UitrustingOvereenkomstActie
}

export interface UitrustingStatusStapDTO {
  titel: string
  tekst: string
  /** ISO-datum of null (nog te gebeuren). */
  moment: string | null
  toon: 'klaar' | 'bezig' | 'later'
}

/** Portal: de eigen controle (`GET /campagnes/:id/uitrusting/mijn`). */
export interface MijnUitrustingscontroleDTO {
  campagne: {
    id: string
    naam: string
    jaar: number
    status: CampagneStatus
    startDatum: string
    deadline: string
  }
  config: UitrustingscontroleConfigDTO
  deelname: {
    id: string
    status: CampagneDeelnameStatus
    versie: number
    ingediendOp: string | null
    namensReden: string | null
    /** De lijst is na de start bijgewerkt en de medewerker heeft dat nog niet gezien. */
    lijstBijgewerkt: boolean
    geenUitrusting: boolean
  }
  /** Wijzigen kan tot en met de deadline zolang de campagne open is. */
  kanWijzigen: boolean
  sets: ControleSetDTO[]
  overeenkomsten: UitrustingOvereenkomstRijDTO[]
  /** De gereedschapsovereenkomst is tekenbaar (sets compleet, of de regel staat uit). */
  magGereedschapTekenen: boolean
  /** Wat het magazijn krijgt, in gewone taal ("momentsleutel aanvullen (servicekoffer)"). */
  magazijnKrijgt: string[]
  tijdlijn: UitrustingStatusStapDTO[]
}

/** Portal: "Wat staat erin" bij een overeenkomst (scherm 4). */
export interface UitrustingOvereenkomstDetailDTO {
  soort: BruikleenTriggerSoort
  label: string
  documentId: string
  documentTitel: string
  /** De BV op het briefpapier ("STREEP Service BV"), of null. */
  entiteitNaam: string | null
  /** De middelen van het document (de trigger-snapshot), zoals bij het genereren. */
  regels: Array<{ omschrijving: string; aantal: number | null; afgevinkt: UitrustingControleRegelStatus | null }>
  uitgifteDatum: string | null
  /** Er is een verschil met wat de medewerker zojuist afvinkte. */
  verschil: boolean
  magTekenen: boolean
  nietTekenbaarReden: string | null
  gemeld: boolean
  /** De actuele versie van het document (voor de PDF via de same-origin proxy), of null. */
  versieNr: number | null
}

/**
 * De samenvatting van de afspraken bij scherm 4 (preview). Bewust algemeen: de
 * volledige overeenkomst (PDF) is leidend, deze tekst vat alleen samen.
 */
export const UITRUSTING_AFSPRAKEN_SAMENVATTING: ReadonlyArray<{ titel: string; tekst: string }> = [
  { titel: 'Gebruik', tekst: 'Alleen voor je werk bij STREEP; je zorgt er netjes voor.' },
  { titel: 'Schade of verlies', tekst: 'Direct melden in de app.' },
  { titel: 'Uit dienst', tekst: 'Inleveren bij het magazijn; ontbrekende zaken worden verrekend volgens de overeenkomst.' },
]

// ─── DTO's: backoffice ────────────────────────────────────────────────────────

export interface UitrustingRechtenDTO {
  inzien: boolean
  /** Aanmaken, wijzigen, vrijgeven, sluiten (HR/ADMIN of ASSETS volledig). */
  inrichten: boolean
  /** Ontbrekend afhandelen en de uitgiftelijst (VOORRAAD schrijven, magazijn). */
  afhandelen: boolean
  /** Overeenkomsten opnieuw genereren (ASSETS schrijven of HR). */
  overeenkomsten: boolean
  /** "Namens" vastleggen (HR/ADMIN, magazijn, of de teamleider van de medewerker). */
  namens: boolean
  /** Alleen het eigen team (teamleider). */
  alleenEigenTeam: boolean
}

export interface UitrustingKpiDTO {
  doelgroep: number
  ingediend: number
  nietGestartOfConcept: number
  onderdelenGemeld: number
  setsNietOntvangen: number
  overeenkomstenGetekend: number
  overeenkomstenOpen: number
}

export interface UitrustingOvereenkomstTellerDTO {
  soort: BruikleenTriggerSoort
  label: string
  getekend: number
  totaal: number
  wachtOpAanvulling: number
  gemeld: number
}

export interface UitrustingscontroleDetailDTO {
  id: string
  naam: string
  jaar: number
  status: CampagneStatus
  startDatum: string
  deadline: string
  handmatigOpenen: boolean
  doelgroep: 'ALLE_MEDEWERKERS' | 'MET_UITRUSTING'
  entityIds: string[]
  departmentIds: string[]
  config: UitrustingscontroleConfigDTO
  vrijgegevenOp: string | null
  verwerktOp: string | null
  eindrapportDocumentId: string | null
  kpis: UitrustingKpiDTO
  uitgiftelijstPerSoort: Array<{ soort: UitrustingSetSoort; label: string; aantal: number }>
  overeenkomstenPerSoort: UitrustingOvereenkomstTellerDTO[]
  achterblijvers: { nietGestart: number; concept: number; herinnerd: string[] }
  rechten: UitrustingRechtenDTO
}

export interface UitrustingMedewerkerRijDTO {
  deelnameId: string
  employeeId: string
  employeeNaam: string
  departmentNaam: string | null
  entityNaam: string | null
  status: CampagneDeelnameStatus
  ingediendOp: string | null
  namens: boolean
  sets: Array<{ soort: UitrustingSetSoort; titel: string; samenvatting: string; toon: ControleSetDTO['samenvattingToon'] }>
  openMeldingen: number
  overeenkomstenGetekend: number
  overeenkomstenTotaal: number
  vervallenReden: string | null
}

export interface UitrustingMedewerkerDetailDTO {
  employeeId: string
  employeeNaam: string
  deelnameStatus: CampagneDeelnameStatus
  ingediendOp: string | null
  namensReden: string | null
  sets: ControleSetDTO[]
  overeenkomsten: UitrustingOvereenkomstRijDTO[]
}

export type UitrustingOntbrekendActie = UitrustingAfhandelActie | 'LEDGER'

export interface UitrustingOntbrekendRegelDTO {
  regelId: string
  setId: string
  setSoort: UitrustingSetSoort
  setTitel: string
  employeeId: string
  employeeNaam: string
  departmentNaam: string | null
  onderdeel: string
  detail: string | null
  serienummer: string | null
  aantal: number
  /** Wat aanvullen of vervangen boekt: bij bulk op naam alleen het afgeboekte aantal (0 = eerst afboeken), anders `aantal`. */
  aanvulAantal: number
  melding: UitrustingMelding
  gemeldOp: string | null
  toelichting: string | null
  fotoAantal: number
  voorraadArtikelId: string | null
  uniekItemId: string | null
  /** Vrij op voorraad (alle locaties), of null zonder voorraadkoppeling. */
  voorraadVrij: number | null
  /** Staat op naam van de medewerker (een exemplaar, of bulk zoals losse accu's): eerst "Zoeken in ledger". */
  opNaam: boolean
  /** Waar de regel vandaan komt (BULK = losse accu's en laders op naam). */
  bron: UitrustingRegelBron
  /** De set staat niet meer op naam: alleen nog afhandelen, niets meer in de set boeken. */
  bronVerdwenen: boolean
  /** Van de koffer is niets als aanwezig afgevinkt: eerst bevestigen dat hij bij de medewerker is. */
  kofferOnzeker: boolean
  afhandeling: UitrustingAfhandeling | null
  afgehandeldOp: string | null
  afhandelReden: string | null
  /** Welke acties hier zinvol zijn (de API weigert de rest). */
  acties: UitrustingOntbrekendActie[]
}

export interface UitrustingOntbrekendGroepDTO {
  sleutel: string
  titel: string
  detail: string | null
  voorraadArtikelId: string | null
  /** Tellers over de VOLLEDIGE gefilterde set, niet over de pagina. */
  mist: number
  defect: number
  nietOntvangen: number
  voorraadVrij: number | null
  minimum: number | null
  /** Hoeveel er nu uit voorraad kan en hoeveel besteld moet worden (groepsactie). */
  uitVoorraad: number
  teBestellen: number
}

export interface UitrustingOntbrekendOverzichtDTO {
  groepeer: 'onderdeel' | 'medewerker'
  groepen: UitrustingOntbrekendGroepDTO[]
  regels: Array<UitrustingOntbrekendRegelDTO & { groepSleutel: string }>
  /** Losse opmerkingen per set (EXTRA bij het magazijn). */
  opmerkingen: Array<{ setId: string; setTitel: string; employeeId: string; employeeNaam: string; opmerking: string }>
}

export interface UitrustingUitgifteRijDTO {
  setId: string
  soort: UitrustingSetSoort
  soortLabel: string
  employeeId: string
  employeeNaam: string
  /** NIET_ONTVANGEN of WEL_IN_BEZIT. */
  status: UitrustingControleSetStatus
  toelichting: string | null
  gemeldOp: string | null
  samenstellingId: string | null
  samenstellingNummer: string | null
  samenstellingStatus: string | null
  afgehandeldOp: string | null
  afhandelReden: string | null
}

export interface UitrustingOvereenkomstOverzichtRijDTO {
  employeeId: string
  employeeNaam: string
  soort: BruikleenTriggerSoort
  label: string
  status: UitrustingOvereenkomstStatus
  documentId: string | null
  meldingId: string | null
  meldingToelichting: string | null
}

export interface UitrustingKeuringRijDTO {
  regelId: string
  employeeId: string
  employeeNaam: string
  setTitel: string
  onderdeel: string
  serienummer: string | null
  uniekItemId: string | null
  /** ISO-instant van de volgende keuring (of de houdbaarheid). */
  keuringTot: string
  verlopen: boolean
  dagen: number
}

export interface UitrustingAuditRijDTO {
  id: string
  moment: string
  actie: string
  actieLabel: string
  actorNaam: string
  employeeNaam: string | null
  onderwerp: string | null
  reden: string | null
  namens: boolean
}

export const UITRUSTING_AUDIT_ACTIE_LABELS: Record<string, string> = {
  STATUS_GEZET: 'Onderdeel afgevinkt of gemeld',
  ALLES_AANWEZIG: 'Alles aanwezig afgevinkt',
  REST_NIET_ONTVANGEN: 'Rest van de set als niet ontvangen gemeld',
  SET_GEMELD: 'Set gemeld',
  OPMERKING: 'Opmerking bij de set',
  QR_BEVESTIGD: 'QR van de koffer gescand',
  WISSEL_GEMELD: 'Andere koffer gescand (wissel gemeld)',
  INGEDIEND: 'Controle ingediend',
  NAMENS_INGEDIEND: 'Controle namens de medewerker vastgelegd',
  AFGEHANDELD: 'Melding afgehandeld',
  LEDGER_BESLUIT: 'Besluit na zoeken in ledger',
  SET_AFGEHANDELD: 'Set afgehandeld',
  SAMENSTELOPDRACHT: 'Samenstel-opdracht gemaakt',
  OVEREENKOMST_GEMELD: 'Overeenkomst gemeld: klopt niet',
  OVEREENKOMST_HERGENEREERD: 'Overeenkomst opnieuw opgesteld',
  OVEREENKOMST_INGETROKKEN: 'Overeenkomst ingetrokken tot de set compleet is',
  OVEREENKOMST_OPGESTELD: 'Overeenkomst opgesteld',
  LIJST_BIJGEWERKT: 'Lijst bijgewerkt',
  GEEN_UITRUSTING: 'Geen uitrusting: deelname vervallen',
  CAMPAGNE_VERWERKT: 'Campagne gesloten en verwerkt',
}

/**
 * Waar "Zoeken in ledger" over gaat: een exemplaar op naam, bulk op naam (losse accu's
 * en laders, met een aantal) of de koffer zelf (van de koffer is niets als aanwezig
 * afgevinkt, integrator-review H1).
 */
export type UitrustingLedgerDoel = 'EXEMPLAAR' | 'BULK' | 'KOFFER'

export interface UitrustingLedgerZoekDTO {
  regelId: string
  doel: UitrustingLedgerDoel
  onderdeel: string
  serienummer: string | null
  employeeNaam: string
  /** Het exemplaar, of bij doel KOFFER de koffer zelf. */
  uniekItem: { id: string; label: string; status: string; houderNaam: string | null; inKoffer: string | null } | null
  /** Bij doel BULK: hoeveel er nu op naam staat (het maximum om af te boeken). */
  bulk: { artikelId: string; opNaam: number } | null
  tijdlijn: Array<{ moment: string; soort: string; omschrijving: string; van: string | null; naar: string | null; reden: string | null; bron: string }>
}

export interface UitrustingLedgerBesluitDTO {
  doel: UitrustingLedgerDoel
  /** De afhandeling van de melding; null bij een bevestigde koffer (de meldingen blijven open om aan te vullen). */
  afhandeling: UitrustingAfhandeling | null
}
